import { Component, OnDestroy, OnInit } from '@angular/core';
import { Observable, Subject, Subscription, from, merge, of } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, filter, map, switchMap, tap } from 'rxjs/operators';
import { AuthService } from '../../../../services/auth.service';
import { CopomexService } from '../../../../services/copomex.service';
import { NeighborhoodService } from '../../../../services/neighborhood.service';
import { ZipCode } from '../../../../models/zip-code.model';

@Component({
  selector: 'app-address-page',
  templateUrl: './address-page.component.html',
  styleUrl: './address-page.component.scss'
})
export class AddressPageComponent implements OnInit, OnDestroy {

  // Bandera de "ya se intentó guardar". Los errores no se pintan antes del
  // primer intento para no recibir al usuario con la pantalla en rojo.
  public submitted: boolean = false;

  // Mensaje de error por campo. Cadena vacía = campo sin error.
  // Se consulta desde el HTML para aplicar 'is-invalid' y pintar el
  // <div class="invalid-feedback"> correspondiente.
  public errors: { [field: string]: string } = {
    street: '',
    city: '',
    state: '',
    zipCode: ''
  };

  // Estado de la consulta a Copomex, a nivel de campo (no global).
  // searchingZipCode  → hay una petición en vuelo
  // zipCodeNotFound   → la API respondió 200 con la lista vacía
  // zipCodeSearchFailed → error de red o del servidor
  // Los tres son excluyentes en la práctica y se pintan junto al input de
  // código postal, nunca como toast ni como overlay global.
  public searchingZipCode: boolean = false;
  public zipCodeNotFound: boolean = false;
  public zipCodeSearchFailed: boolean = false;

  // El botón "Buscar" pulsado con un valor que no llega a 5 dígitos levanta
  // esta bandera para pintar el error de formato del código postal SIN tener
  // que encender `submitted`.
  //
  // Encender `submitted` desde el botón sería mentir: esa bandera significa
  // "ya se intentó guardar" y es la que autoriza a pintar en rojo la calle, la
  // ciudad y el estado. Pulsar Buscar no es intentar guardar, y no tiene por
  // qué señalar campos que no participan en la búsqueda.
  public zipCodeFormatWarned: boolean = false;

  // Código postal mexicano: exactamente 5 dígitos.
  // Se acepta cero inicial (01000, Centro CDMX), por eso se valida como
  // texto con regex y no como número.
  private readonly zipCodePattern: RegExp = /^\d{5}$/;

  // Canal por el que viajan los códigos postales que el usuario TECLEA.
  //
  // ⚠️ REGLA CRÍTICA: LA CONSULTA NUNCA SE DISPARA AL CARGAR LA PÁGINA ⚠️
  //
  // Este Subject se alimenta EXCLUSIVAMENTE desde el evento (input) del campo
  // de código postal en el HTML. Nada lo alimenta desde ngOnInit, ni desde el
  // valor que ya trae currentNeighborhood, ni desde ningún otro lado.
  //
  // El motivo: la página abre con la dirección YA GUARDADA en base de datos, y
  // varias colonias tienen capturados valores que no coinciden con el
  // municipio de SEPOMEX. "Ojo de Agua", por ejemplo, es una localidad, no un
  // municipio; SEPOMEX devolvería "Tecámac". Si la consulta corriera al
  // inicializar, sobrescribiría en silencio datos que nadie pidió cambiar, y
  // el usuario ni siquiera se enteraría hasta después de guardar.
  //
  // Por eso NO se usa ngModelChange sobre el modelo ni un valueChanges sobre
  // el estado: solo eventos de escritura reales del usuario.
  private zipCodeTyped: Subject<string> = new Subject<string>();

  // Canal del botón "Buscar": consulta A PETICIÓN EXPLÍCITA del usuario.
  //
  // ⚠️ NO PUEDE VIAJAR POR zipCodeTyped ⚠️
  // Ese canal pasa por distinctUntilChanged(), y el caso principal de uso del
  // botón es justamente pulsarlo SIN haber cambiado el código postal: la
  // página abre con la dirección ya guardada, la consulta nunca corre al
  // cargar, y el usuario quiere forzarla porque la ciudad o el estado están
  // mal. Emitir por zipCodeTyped haría que distinctUntilChanged se tragara la
  // emisión en silencio y el botón pareciera roto.
  //
  // Tampoco es una segunda suscripción independiente: ambos canales se unen
  // con merge() ANTES del filter y del switchMap, de modo que sigue habiendo
  // una sola petición en vuelo y un clic cancela una búsqueda automática
  // anterior (y una búsqueda automática cancela la del clic).
  private zipCodeSearchRequested: Subject<string> = new Subject<string>();

  // Se da de baja en ngOnDestroy. Es la única suscripción del componente.
  private zipCodeSubscription?: Subscription;

  constructor(
    public authService: AuthService,
    public copomexService: CopomexService,
    public neighborhoodService: NeighborhoodService
  ) {}

  ngOnInit(): void {

    // Rama del AUTORRELLENO: lo que el usuario teclea. Es la única que lleva
    // debounce y distinctUntilChanged; el botón se salta las dos.
    const typedZipCodes: Observable<string> = this.zipCodeTyped.pipe(

      // El usuario teclea 5 dígitos en menos de medio segundo; sin debounce
      // se dispararían hasta 5 peticiones para llegar al mismo resultado.
      debounceTime(400),

      // Reescribir el mismo código postal (borrar un dígito y volver a
      // escribirlo) no vuelve a consultar.
      distinctUntilChanged()
    );

    // merge() une las dos fuentes ANTES del filter y del switchMap: el clic y
    // la escritura comparten el mismo operador de cancelación, así que jamás
    // hay dos peticiones en vuelo ni respuestas que lleguen desordenadas.
    this.zipCodeSubscription = merge(typedZipCodes, this.zipCodeSearchRequested).pipe(

      // El código postal va como path parameter y la API exige exactamente 5
      // caracteres: con menos responde 422. Aquí se corta antes de pedir.
      filter((zipCode) => this.zipCodePattern.test(zipCode)),

      tap(() => {
        this.searchingZipCode = true;
        this.zipCodeNotFound = false;
        this.zipCodeSearchFailed = false;
      }),

      // switchMap NO es opcional.
      // La API corre en Lambda sin concurrencia provisionada: el arranque en
      // frío puede tardar de 1 a 3 segundos. Si el usuario corrige un dígito
      // mientras la primera petición sigue en vuelo, habría dos respuestas en
      // camino y podrían llegar desordenadas, dejando en pantalla la ciudad
      // del código postal viejo. switchMap cancela la anterior: siempre gana
      // la última que el usuario escribió.
      switchMap((zipCode) =>
        from(this.copomexService.searchByZipCode(zipCode)).pipe(
          map((zipCodes) => ({ zipCodes, failed: false })),

          // El catchError va DENTRO del switchMap a propósito: si estuviera
          // fuera, el primer error de red mataría la suscripción y el campo
          // dejaría de consultar durante el resto de la vida de la página.
          catchError(() => of({ zipCodes: [] as ZipCode[], failed: true }))
        )
      )

    ).subscribe((result) => {

      this.searchingZipCode = false;

      if(result.failed) {
        // Error de red o del servidor. NO se toca Ciudad ni Estado: lo que el
        // usuario ya tenía capturado sigue siendo válido.
        this.zipCodeSearchFailed = true;
        return;
      }

      // Un código postal inexistente NO devuelve 404: devuelve 200 con la
      // lista vacía. Esta es la única forma de detectar "no encontrado".
      if(result.zipCodes.length === 0) {
        // Tampoco se toca Ciudad ni Estado aquí.
        this.zipCodeNotFound = true;
        return;
      }

      this.fillAddressFromZipCode(result.zipCodes[0]);
    });

  }

  ngOnDestroy(): void {
    this.zipCodeSubscription?.unsubscribe();
    this.zipCodeTyped.complete();
    this.zipCodeSearchRequested.complete();
  }

  /**
   * GETTER zipCodeErrorVisible
   *
   * Decide si se pinta el error del campo de código postal.
   *
   * Dos disparadores, uno viejo y uno nuevo:
   *   submitted            → ya se intentó guardar (comportamiento existente)
   *   zipCodeFormatWarned  → se pulsó Buscar con menos de 5 dígitos
   *
   * En ambos casos sigue exigiéndose que haya un mensaje que pintar, así que
   * el campo nunca se marca en rojo sin texto que lo explique.
   */
  get zipCodeErrorVisible(): boolean {
    return (this.submitted || this.zipCodeFormatWarned) && this.errors['zipCode'] !== '';
  }

  /*
   ██████╗ ██████╗ ██████╗ ██╗ ██████╗  ██████╗
  ██╔════╝██╔═══██╗██╔══██╗██║██╔════╝ ██╔═══██╗
  ██║     ██║   ██║██║  ██║██║██║  ███╗██║   ██║
  ██║     ██║   ██║██║  ██║██║██║   ██║██║   ██║
  ╚██████╗╚██████╔╝██████╔╝██║╚██████╔╝╚██████╔╝
   ╚═════╝ ╚═════╝ ╚═════╝ ╚═╝ ╚═════╝  ╚═════╝

  */
  /**
   * FUNCTION onZipCodeInput
   *
   * Entrada AUTOMÁTICA de la consulta a Copomex. Se engancha al evento
   * (input) del campo de código postal, es decir, solo corre cuando el
   * usuario escribe. Ver el comentario de `zipCodeTyped`.
   *
   * La otra entrada es searchZipCode(), el botón "Buscar", que es explícita.
   *
   * También limpia el error de validación del propio campo, igual que
   * clearError() hace en los demás inputs.
   *
   * @param value texto actual del campo de código postal
   */
  onZipCodeInput(value: string) {

    this.clearError('zipCode');

    // Mientras el usuario sigue escribiendo, los mensajes de la búsqueda
    // anterior dejan de aplicar. El aviso de formato del botón tampoco: el
    // valor que lo provocó ya no es el que está en pantalla.
    this.zipCodeNotFound = false;
    this.zipCodeSearchFailed = false;
    this.zipCodeFormatWarned = false;

    this.zipCodeTyped.next((value ?? '').trim());
  }

  /**
   * FUNCTION searchZipCode
   *
   * Entrada EXPLÍCITA de la consulta a Copomex: el botón "Buscar".
   *
   * Existe porque la consulta automática nunca corre al abrir la pantalla (es
   * una regla deliberada, para no sobrescribir en silencio direcciones ya
   * guardadas). Si el código postal guardado es correcto pero la ciudad o el
   * estado están mal, sin este botón la única forma de forzar la consulta
   * sería borrar un dígito y volver a escribirlo.
   *
   * Emite por `zipCodeSearchRequested`, NO por `zipCodeTyped`: ver el
   * comentario de esa propiedad.
   *
   * El botón NO se deshabilita con menos de 5 dígitos —mismo criterio que el
   * botón Guardar—: se pulsa y se pinta el error de formato junto al campo.
   * Deshabilitarlo dejaría al usuario adivinando qué le falta.
   */
  searchZipCode() {

    const zipCode = (this.neighborhoodService.currentNeighborhood?.zipCode ?? '').trim();

    // Se pidió una búsqueda nueva: lo que dijera la anterior ya no aplica.
    this.zipCodeNotFound = false;
    this.zipCodeSearchFailed = false;

    if(!this.zipCodePattern.test(zipCode)) {
      // Mismo texto que usa validate() para esta regla. Vacío y "123" caen
      // los dos aquí: en ambos casos lo que falta es un código de 5 dígitos.
      this.errors['zipCode'] = 'El código postal debe tener exactamente 5 dígitos';
      this.zipCodeFormatWarned = true;
      return;
    }

    this.zipCodeFormatWarned = false;
    this.clearError('zipCode');

    this.zipCodeSearchRequested.next(zipCode);
  }

  /**
   * FUNCTION fillAddressFromZipCode
   *
   * Escribe Ciudad y Estado a partir del resultado de SEPOMEX.
   *
   * MAPEO DE CAMPOS —decisión tomada, no cambiar sin motivo:
   *   Estado ← state
   *   Ciudad ← municipality  (NO city)
   *
   * `city` puede llegar como cadena vacía en localidades sin ciudad asignada,
   * mientras que `municipality` siempre viene poblado. Además `municipality`
   * es lo que coincide con los datos ya capturados en base de datos: Pachuca
   * de Soto, Lagos de Moreno y Tlalpan son municipios, no ciudades.
   *
   * Todos los elementos del array comparten `state` y `municipality` (varían
   * en la colonia), así que basta con leer el primero.
   *
   * El autorrelleno NO guarda: solo escribe en el modelo en memoria. El
   * usuario sigue teniendo que pulsar Guardar. Y Ciudad y Estado siguen
   * siendo editables, porque los datos históricos no siempre coinciden con
   * SEPOMEX y el usuario debe poder corregirlos.
   *
   * @param zipCode primer resultado devuelto por Copomex
   */
  private fillAddressFromZipCode(zipCode: ZipCode) {

    const neighborhood = this.neighborhoodService.currentNeighborhood;

    if(!neighborhood) {
      return;
    }

    neighborhood.city = zipCode.municipality;
    neighborhood.state = zipCode.state;

    // Si los campos venían marcados en rojo por la validación de Guardar,
    // el autorrelleno los deja válidos: el error se retira.
    this.clearError('city');
    this.clearError('state');
  }

  /*
  ██╗   ██╗ █████╗ ██╗     ██╗██████╗  █████╗ ████████╗███████╗
  ██║   ██║██╔══██╗██║     ██║██╔══██╗██╔══██╗╚══██╔══╝██╔════╝
  ██║   ██║███████║██║     ██║██║  ██║███████║   ██║   █████╗
  ╚██╗ ██╔╝██╔══██║██║     ██║██║  ██║██╔══██║   ██║   ██╔══╝
   ╚████╔╝ ██║  ██║███████╗██║██████╔╝██║  ██║   ██║   ███████╗
    ╚═══╝  ╚═╝  ╚═╝╚══════╝╚═╝╚═════╝ ╚═╝  ╚═╝   ╚═╝   ╚══════╝

  */
  /**
   * FUNCTION validate
   *
   * Las cuatro columnas de dirección son nullable=False en base de datos, pero
   * el backend solo asigna el campo "si viene distinto de None": una cadena
   * vacía SÍ se guardaría y dejaría la colonia sin dirección. Por eso el
   * bloqueo tiene que ocurrir aquí, del lado del cliente.
   *
   * @returns true si los cuatro campos son válidos
   */
  validate(): boolean {

    const neighborhood = this.neighborhoodService.currentNeighborhood;

    if(!neighborhood) {
      return false;
    }

    this.errors = {
      street: '',
      city: '',
      state: '',
      zipCode: ''
    };

    // Solo espacios cuenta como vacío: el backend guardaría "   " sin quejarse.
    if(!neighborhood.street || neighborhood.street.trim() === '') {
      this.errors['street'] = 'Por favor, ingrese la calle del conjunto';
    }

    if(!neighborhood.city || neighborhood.city.trim() === '') {
      this.errors['city'] = 'Por favor, ingrese la ciudad del conjunto';
    }

    if(!neighborhood.state || neighborhood.state.trim() === '') {
      this.errors['state'] = 'Por favor, ingrese el estado del conjunto';
    }

    if(!neighborhood.zipCode || neighborhood.zipCode.trim() === '') {
      this.errors['zipCode'] = 'Por favor, ingrese el código postal del conjunto';
    } else if(!this.zipCodePattern.test(neighborhood.zipCode.trim())) {
      this.errors['zipCode'] = 'El código postal debe tener exactamente 5 dígitos';
    }

    return Object.values(this.errors).every((message) => message === '');
  }

  /**
   * FUNCTION clearError
   *
   * Limpia el error de un campo en cuanto el usuario lo escribe. Sin esto el
   * mensaje se quedaría pegado aunque ya estuviera corregido.
   *
   * @param field nombre del campo (street, city, state, zipCode)
   */
  clearError(field: string) {
    this.errors[field] = '';
  }

  /*
  ███████╗ █████╗ ██╗   ██╗███████╗
  ██╔════╝██╔══██╗██║   ██║██╔════╝
  ███████╗███████║██║   ██║█████╗
  ╚════██║██╔══██║╚██╗ ██╔╝██╔══╝
  ███████║██║  ██║ ╚████╔╝ ███████╗
  ╚══════╝╚═╝  ╚═╝  ╚═══╝  ╚══════╝

  */
  /**
   * FUNCTION save
   *
   * El [(ngModel)] ya mutó currentNeighborhood, y el servicio serializa ese
   * objeto completo, así que update() no recibe argumentos.
   * El servicio también se encarga del spinner global y de los toasts de
   * éxito/error, por eso aquí no hay ninguno de los dos.
   */
  save() {

    this.submitted = true;

    if(!this.validate()) {
      return;
    }

    this.neighborhoodService.update();
  }

}
